import { useMutation } from '@tanstack/react-query';

import { IUpload } from '@impler/client';
import { IErrorObject } from '@impler/shared';

import { notifier, ParentWindow } from '@util';
import { useAPIState } from '@store/api.context';
import { useAppState } from '@store/app.context';
import { useImplerState } from '@store/impler.context';

interface IUseManualEntryUploadProps {
  onCreated: () => void;
}

/**
 * Creates the upload record for a MANUAL_ENTRY flow that was requested on show.
 *
 * <p>Normally Phase 1 does this: its "Directly enter your data" button submits the form
 * with no file, and only in that mutation's onSuccess is `uploadInfo` set. Skipping the
 * upload screen therefore skips the thing that mints the upload -- without this the grid
 * mounts with no `uploadInfo._id` and immediately requests `/upload/undefined/columns`.
 *
 * <p>Deliberately separate from usePhase1 rather than extracted out of it: the file-upload
 * flow is the widget's main path and is left untouched.
 */
export function useManualEntryUpload({ onCreated }: IUseManualEntryUploadProps) {
  const { api } = useAPIState();
  const { templateId, authHeaderValue, extra } = useImplerState();
  const { setUploadInfo, schema, output, importId, imageSchema, maxRecords } = useAppState();

  const { mutate: createManualEntryUpload, isLoading: isCreatingManualEntryUpload } = useMutation<
    IUpload,
    IErrorObject,
    void,
    [string]
  >(
    ['manual-entry-upload'],
    () =>
      api.uploadFile({
        templateId: templateId as string,
        authHeaderValue,
        extra,
        schema,
        output,
        importId,
        imageSchema,
        maxRecords,
      }),
    {
      onSuccess(uploadData) {
        // follow-up listens for this to know the widget is live; Phase 1 fires it too.
        ParentWindow.UploadStarted({ templateId: uploadData._templateId, uploadId: uploadData._id });
        setUploadInfo(uploadData);
        onCreated();
      },
      onError(error: IErrorObject) {
        notifier.showError({ title: error.error, message: error.message });
      },
    }
  );

  return { createManualEntryUpload, isCreatingManualEntryUpload };
}
